import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { Button } from '../components/ui/button';
import { Menu, X, LayoutDashboard, ClipboardList, History, Star, Building2, UserCog, ClipboardCheck, ShieldCheck, LogOut, DollarSign, Zap, Settings, CalendarPlus, Users, Megaphone, BarChart3, FolderOpen, ChevronDown, Package, UtensilsCrossed, Wine, ShieldAlert, ChefHat, Receipt, MessageSquare, Sparkles, Gift, Activity, TrendingUp, Bell, Store, FileSpreadsheet } from 'lucide-react';
import { DiscussionsBell } from '../pages/ActionCommCenter';
import AiAlertBanner from './AiAlertBanner';
import { modulesForPath, MANAGER_LIKE_ROLES, ADMIN_LIKE_ROLES, SCOPED_ADMIN_ROLES } from '../config/modules';
import { CHILD_TO_PARENT } from '../config/feature_parents';
import { deriveBrandVars } from '../lib/tenantBrand';

// Manager nav grouped by operational bucket (mirrors admin structure for consistency)
// iter167 — every item carries the tenant feature_flag it depends on so the
// sidebar is purely registry-driven. `feature_default: false` flags opt-in
// modules (hidden until super-admin enables for the tenant). Items without
// a feature_flag are always visible (legacy modules, no tenant toggle).
const managerNavGroups = [
  { label: 'Overview', items: [
    { to: '/manager/overview', label: 'Overview', icon: BarChart3, feature_flag: 'dashboard' },
    { to: '/manager/sales-analytics', label: 'Sales Analytics', icon: TrendingUp, feature_flag: 'dashboard' },
    { to: '/manager/reviews', label: 'Review & Discussion', icon: FileSpreadsheet, feature_flag: 'review_discussion', feature_default: false },
    { to: '/manager/entry', label: 'Daily Business Report', icon: ClipboardList },
  ]},
  { label: 'Operations', items: [
    { to: '/manager/kitchen', label: 'Kitchen Closing', icon: UtensilsCrossed, feature_flag: 'kitchen_closing' },
    { to: '/manager/bar', label: 'Bar / Beverage Closing', icon: Wine, feature_flag: 'bar_closing' },
    { to: '/manager/production', label: 'Production', icon: Package, feature_flag: 'production_planner' },
    { to: '/manager/petty-cash', label: 'Petty Cash', icon: Receipt, feature_flag: 'petty_cash' },
    { to: '/manager/checklist', label: 'Checklist', icon: ClipboardCheck, feature_flag: 'checklist' },
    { to: '/manager/tickets', label: 'Tickets', icon: ShieldAlert },
    { to: '/manager/ops-command', label: 'Command Centre', icon: Activity },
    { to: '/manager/compliance-intelligence', label: 'Compliance Intelligence', icon: TrendingUp },
    { to: '/manager/maintenance', label: 'Maintenance', icon: Zap, feature_flag: 'maintenance' },
  ]},
  { label: 'Financials', items: [
    { to: '/manager/pnl', label: 'P&L', icon: DollarSign, feature_flag: 'pnl_live' },
    { to: '/manager/cost-insights', label: 'Cost Insights', icon: BarChart3, feature_flag: 'cost_insights' },
  ]},
  { label: 'Menu & Inventory', items: [
    // iter280 — Menu Availability. Nav is always visible for managers;
    // the page itself shows a helpful "Menu isn't enabled yet" card when
    // the manager's outlet doesn't have the digital menu turned on.
    { to: '/manager/menu-availability', label: 'Menu Availability', icon: UtensilsCrossed },
    { to: '/manager/recipes', label: 'Recipes', icon: ChefHat, feature_flag: 'recipe_module' },
  ]},
  { label: 'Customers', items: [
    { to: '/manager/customer-feedback', label: 'Customer Feedback', icon: Star, feature_flag: 'customer_feedback' },
    { to: '/manager/reservations', label: 'Reservations', icon: CalendarPlus },
    { to: '/manager/loyalty', label: 'Loyalty Redemption', icon: Gift, feature_flag: 'loyalty', feature_default: false },
  ]},
  { label: 'Communication', items: [
    { to: '/manager/action-comm', label: 'Action Comm. Center', icon: MessageSquare, feature_flag: 'communication' },
  ]},
  { label: 'Marketing', items: [
    { to: '/manager/marketing', label: 'Marketing', icon: Megaphone, feature_flag: 'marketing' },
  ]},
  { label: 'Commerce', group_flag: 'commerce_home', items: [
    // POS Sprint 1 · Manager-shell entry for the POS terminal. Cashiers/
    // stewards operate the terminal via the in-app PIN gate; PosSettings
    // stays admin-only, so it is intentionally omitted here.
    { to: '/pos', label: 'POS Terminal', icon: Store,
      feature_flag: 'pos', feature_default: false },
  ]},
  { label: 'Insights', items: [
    { to: '/manager/insights', label: 'Insights', icon: BarChart3, feature_flag: 'reports' },
    { to: '/manager/history', label: 'History', icon: History },
  ]},
];

const managerLinks = managerNavGroups.flatMap(g => g.items);

// Admin nav grouped by business function. iter167 — items carry inline
// feature_flag declarations; the sidebar is fully registry-driven.
// iter180 — each group now declares an explicit `group_flag` (parent feature
// key). When parent is OFF the entire group disappears AND its children's
// individual flags are cascaded-off (see /app/frontend/src/config/feature_parents.js).
const adminNavGroups = [
  {
    label: 'Overview', group_flag: 'overview_home', items: [
      { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, feature_flag: 'dashboard' },
      { to: '/admin/reviews', label: 'Review & Discussion', icon: FileSpreadsheet, feature_flag: 'review_discussion', feature_default: false },
    ]
  },
  {
    label: 'Financials', group_flag: 'financials_home', items: [
      { to: '/admin/insights', label: '5-Minute Dashboard', icon: BarChart3, feature_flag: 'financial_dashboard' },
      { to: '/admin/pnl', label: 'P&L', icon: DollarSign, feature_flag: 'pnl_live' },
      { to: '/admin/cost-insights', label: 'Cost Insights', icon: BarChart3, feature_flag: 'cost_insights' },
      { to: '/admin/petty-cash', label: 'Petty Cash', icon: Receipt, feature_flag: 'petty_cash' },
      // Review Sheet & MBR History deactivated — will be migrated into PnL.
    ]
  },
  {
    label: 'Operations', group_flag: 'operations_home', subgroups: [
      {
        label: 'Compliance',
        items: [
          { to: '/admin/ops-command', label: 'Command Centre', icon: Activity },
          { to: '/admin/compliance', label: 'Compliance', icon: ShieldCheck, feature_flag: 'checklist' },
          { to: '/admin/compliance-intelligence', label: 'Intelligence', icon: TrendingUp },
          { to: '/admin/checklist', label: 'Checklist Management', icon: ClipboardCheck, feature_flag: 'checklist' },
        ],
      },
      {
        label: 'Ticketing',
        items: [
          { to: '/admin/tickets', label: 'Tickets', icon: ShieldAlert },
          { to: '/admin/tickets?tab=failures', label: 'Failure Register', icon: ClipboardList },
          { to: '/admin/notification-rules', label: 'Notifications', icon: Bell },
          { to: '/admin/ticket-settings', label: 'Ticket Settings', icon: Settings },
        ],
      },
      {
        label: 'Maintenance',
        items: [
          { to: '/admin/maintenance', label: 'Maintenance', icon: Zap, feature_flag: 'maintenance' },
        ],
      },
    ],
    items: [
      // Un-grouped: Inventory / Recipes / Production live below the divider.
      { to: '/admin/inventory', label: 'Inventory', icon: Package, feature_flag: 'inventory_module' },
      { to: '/admin/recipes', label: 'Recipes', icon: ChefHat, feature_flag: 'recipe_module' },
      { to: '/admin/production', label: 'Production', icon: Package, feature_flag: 'production_planner' },
      { to: '/admin/opening-stock', label: 'Opening Stock', icon: Package, feature_flag: 'inventory_module' },
      { to: '/admin/override-requests', label: 'Overrides', icon: ShieldAlert, feature_flag: 'approvals' },
    ]
  },
  {
    label: 'Marketing', group_flag: 'marketing_home', items: [
      { to: '/admin/marketing', label: 'Marketing', icon: Megaphone, feature_flag: 'marketing' },
    ]
  },
  {
    // iter183k — Restaurant Expansion Analysis section. Premium tier hosting
    // Expansion Intelligence and the future Project Feasibility / Investment
    // Planning / Market Intelligence / Franchise Intelligence modules.
    // Defaults OFF; tenant must be on the top-tier plan to see this group.
    label: 'Restaurant Expansion Analysis', group_flag: 'strategy_home', items: [
      { to: '/admin/expansion-intelligence', label: 'Restaurant Expansion Intelligence',
        icon: Sparkles, feature_flag: 'expansion_intelligence', feature_default: false },
    ]
  },
  {
    label: 'Customers', group_flag: 'customers_home', items: [
      { to: '/admin/customers', label: 'Customers', icon: Users, feature_flag: 'crm' },
      { to: '/admin/reservations', label: 'Reservations', icon: CalendarPlus, feature_flag: 'reservations_module' },
      { to: '/admin/customer-experience', label: 'Customer Exp.', icon: Star, feature_flag: 'customer_feedback' },
      // iter167 — Customer Pulse Index (feature-gated by `custom_feedback_forms`).
      // Hidden until the tenant flag is enabled in Super Admin → Features.
      { to: '/admin/cx', label: 'Customer Pulse', icon: Sparkles,
        feature_flag: 'custom_feedback_forms', feature_default: false },
      // Loyalty Phase 1 MVP — opt-in per tenant, hidden until Super Admin enables.
      { to: '/admin/loyalty', label: 'Loyalty', icon: Gift,
        feature_flag: 'loyalty', feature_default: false },
      // Guest Hub NFC — Membership leads captured behind the /hub/:slug NFC tag.
      { to: '/admin/loyalty/leads', label: 'Membership Leads', icon: Users,
        feature_flag: 'loyalty', feature_default: false },
      // Guest Hub NFC — Editable public membership page content per outlet.
      { to: '/admin/loyalty/membership-content', label: 'Membership Content', icon: Sparkles,
        feature_flag: 'loyalty', feature_default: false },
    ]
  },
  {
    label: 'Communication', group_flag: 'communication_home', items: [
      { to: '/admin/action-comm', label: 'Action Comm. Center', icon: MessageSquare, feature_flag: 'communication' },
      // Public-demo lead management — visible only to the RestroPulse
      // operator tenant + super-admins. Runtime filter enforced below.
      { to: '/admin/demo-leads', label: 'Demo Leads', icon: Users,
        operator_only: true },
    ]
  },
  {
    // POS Sprint 1 · Commerce · Terminal + Staff Settings. The whole group
    // is gated by the `commerce_home` parent (mirrors GROUP_PARENTS on the
    // backend), and each item carries the `pos` child feature flag so a
    // single Super Admin toggle controls both cascade paths.
    label: 'Commerce', group_flag: 'commerce_home', items: [
      { to: '/pos', label: 'POS Terminal', icon: Store,
        feature_flag: 'pos', feature_default: false },
      { to: '/pos/settings', label: 'POS Settings', icon: Settings,
        feature_flag: 'pos', feature_default: false },
      { to: '/pos/reports', label: 'POS Reports', icon: BarChart3,
        feature_flag: 'pos', feature_default: false },
    ]
  },
  {
    // iter180 — Organization's group_flag gates user/outlet/audit pages only.
    // Settings is always rendered (no feature_flag), so the dropdown remains
    // accessible for profile / preferences / logout even when parent is OFF.
    label: 'Organization', group_flag: 'organization_home', items: [
      { to: '/admin/managers', label: 'Users', icon: Users, feature_flag: 'user_management' },
      { to: '/admin/outlets', label: 'Outlets', icon: Building2, feature_flag: 'outlet_management' },
      { to: '/admin/portfolios', label: 'Portfolios', icon: FolderOpen, feature_flag: 'outlet_management' },
      { to: '/admin/settings', label: 'Settings', icon: Settings },  // always visible
    ]
  },
];

// Super Admin extra links
const superAdminExtraLinks = [
  { to: '/super/features', label: 'Feature Control', icon: ShieldCheck },
];

// Flat admin links for mobile menu and route matching
const adminLinks = adminNavGroups.flatMap(g => g.items);

function NavDropdown({ group, pathname, badges }) {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ left: 0, top: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const isActive = [...(group.items || []),
                     ...(group.subgroups || []).flatMap(sg => sg.items)]
                     .some(i => pathname === i.to);
  // Aggregate badges for items in this group (only ones we know about).
  const groupBadge = (group.items || []).reduce((s, i) => s + (badges?.[i.to] || 0), 0)
    + (group.subgroups || []).reduce((s, sg) => s + sg.items.reduce((ss, i) => ss + (badges?.[i.to] || 0), 0), 0);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Recompute dropdown anchor when opening so it stays attached even when
  // the parent <nav> is horizontally scrolled (fixes overlap with the
  // right-side identity/logout on desktop widths).
  const openDropdown = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropdownPos({ left: r.left, top: r.bottom });
    }
    setOpen((v) => !v);
  };

  // Single-item groups render as direct link (skip when subgroups exist)
  if ((group.items || []).length === 1 && !(group.subgroups || []).length) {
    const item = group.items[0];
    const Icon = item.icon;
    const active = pathname === item.to;
    const itemBadge = badges?.[item.to] || 0;
    return (
      <Link to={item.to}>
        <button className={`flex items-center gap-1.5 whitespace-nowrap text-[12px] h-8 px-2.5 rounded-md transition-colors
          ${active
            ? 'text-[color:var(--color-primary)] font-semibold bg-[color:var(--color-primary)]/[0.06]'
            : 'text-[#5C6B64] hover:text-[#1A2421] hover:bg-[#F3F5F2]'
          }`}
          data-testid={`nav-group-${group.label.toLowerCase()}`}
        >
          <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
          {group.label}
          {itemBadge > 0 && (
            <span className="ml-0.5 text-[9px] font-bold bg-amber-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
              data-testid={`nav-badge-${item.to.split('/').pop()}`}>
              {itemBadge > 99 ? '99+' : itemBadge}
            </span>
          )}
        </button>
      </Link>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        ref={btnRef}
        onClick={openDropdown}
        className={`flex items-center gap-1 whitespace-nowrap text-[12px] h-8 px-2.5 rounded-md transition-colors
          ${isActive
            ? 'text-[color:var(--color-primary)] font-semibold bg-[color:var(--color-primary)]/[0.06]'
            : 'text-[#5C6B64] hover:text-[#1A2421] hover:bg-[#F3F5F2]'
          }`}
        data-testid={`nav-group-${group.label.toLowerCase()}`}
      >
        {group.label}
        {groupBadge > 0 && (
          <span className="ml-0.5 text-[9px] font-bold bg-amber-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
            data-testid={`nav-group-badge-${group.label.toLowerCase()}`}>
            {groupBadge > 99 ? '99+' : groupBadge}
          </span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed mt-0 bg-white border border-[#E2E8DF] rounded-b-lg shadow-lg z-50 min-w-[220px] py-1"
            style={{ left: dropdownPos.left, top: dropdownPos.top }}
            data-testid={`nav-dropdown-${group.label.toLowerCase()}`}>
            {/* ITER298 — grouped subgroups render first with an uppercase
                label, then the flat items list below a divider. */}
            {(group.subgroups || []).map((sg) => (
              <div key={sg.label} className="mb-1">
                <div
                  className="px-3 pt-2 pb-1 text-[9px] font-semibold uppercase tracking-[0.14em]"
                  style={{ color: 'var(--color-text-muted)' }}
                  data-testid={`nav-subgroup-${sg.label.toLowerCase()}`}
                >
                  {sg.label}
                </div>
                {sg.items.map((item) => {
                  const Icon = item.icon;
                  const active = pathname === item.to;
                  const itemBadge = badges?.[item.to] || 0;
                  return (
                    <Link key={item.to} to={item.to} onClick={() => setOpen(false)}>
                      <div className={`flex items-center gap-2.5 pl-5 pr-3 py-1.5 text-[13px] transition-colors
                        ${active ? 'bg-[color:var(--color-primary)]/5 text-[color:var(--color-primary)] font-semibold' : 'text-[#5C6B64] hover:bg-[#F3F5F2] hover:text-[#1A2421]'}`}
                        data-testid={`nav-${item.to.split('/').pop().split('?')[0]}`}
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                        <span className="flex-1">{item.label}</span>
                        {itemBadge > 0 && (
                          <span className="text-[9px] font-bold bg-amber-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                            {itemBadge > 99 ? '99+' : itemBadge}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ))}
            {(group.subgroups || []).length > 0 && (group.items || []).length > 0 && (
              <div className="my-1 border-t" style={{ borderColor: 'var(--color-border-soft)' }} />
            )}
            {(group.items || []).map(item => {
              const Icon = item.icon;
              const active = pathname === item.to;
              const itemBadge = badges?.[item.to] || 0;
              return (
                <Link key={item.to} to={item.to} onClick={() => setOpen(false)}>
                  <div className={`flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors
                    ${active ? 'bg-[color:var(--color-primary)]/5 text-[color:var(--color-primary)] font-semibold' : 'text-[#5C6B64] hover:bg-[#F3F5F2] hover:text-[#1A2421]'}`}
                    data-testid={`nav-${item.to.split('/').pop()}`}
                  >
                    <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                    <span className="flex-1">{item.label}</span>
                    {itemBadge > 0 && (
                      <span className="text-[9px] font-bold bg-amber-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                        data-testid={`nav-item-badge-${item.to.split('/').pop()}`}>
                        {itemBadge > 99 ? '99+' : itemBadge}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, assignedOutlets, switchOutlet, tenantFeatures, getHeaders, tenantBranding, previewPrimaryColor } = useAuth();
  const { logoUrl: platformLogoUrl, platformName } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [outletDropdownOpen, setOutletDropdownOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [portfolioLogo, setPortfolioLogo] = useState(null);
  // Pending Petty Cash approvals — only fetched for users who can approve.
  // Polled every 60s; surfaces as a badge on the Operations menu and item.
  const [pendingPettyCash, setPendingPettyCash] = useState(0);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // Feature-to-link mapping. iter167 — every nav item declares `feature_flag`
  // inline on its item dict (admin AND manager nav). The resolver looks it up
  // directly from the registry below. The legacy `linkToFeature` map is kept
  // ONLY as a fallback for any path that happens to hit the resolver without
  // an inline declaration (defensive — empty in practice).
  const linkToFeature = {};
  // Lookup inline feature_flag from BOTH admin + manager nav configs.
  const _navItemByPath = useMemo(() => {
    const m = {};
    adminNavGroups.forEach((g) => g.items.forEach((i) => { m[i.to] = i; }));
    managerNavGroups.forEach((g) => g.items.forEach((i) => { m[i.to] = i; }));
    return m;
  }, []);
  // Admin-management-only links — these aren't user-facing modules, just live in
  // the admin shell. Hidden for non-admin roles entirely.
  const ADMIN_ONLY_LINKS = new Set([
    '/admin/dashboard', '/admin/managers', '/admin/outlets', '/admin/fixed-costs',
    '/admin/portfolios', '/admin/month-lock', '/admin/data-health',
    '/admin/override-requests', '/admin/alert-settings', '/admin/compliance',
    '/admin/customers',
  ]);
  // ITER293 — Production Head is a specialist role: their admin shell is
  // ITER308-B · Scoped-admin (production_head / maintenance_manager)
  //   sidebar is now assignment-driven. Filter by user's
  //   assigned_modules — same mechanism for both roles, no whitelist.
  //   Legacy production_head with an empty list still sees the
  //   recipes/pricing/production trio (behaviour-identical migration).
  const LEGACY_PRODUCTION_HEAD_MODS = ['recipes', 'pricing', 'production'];
  const isFeatureAllowed = (to) => {
    const item = _navItemByPath[to];
    const f = item?.feature_flag || linkToFeature[to];
    if (!f) return true;
    if (user?.role === 'super_admin') return true;
    const stored = tenantFeatures?.[f];
    let baseEnabled;
    if (stored === undefined) {
      // No value loaded yet → fall back to the item's inline default
      // (true for legacy default-on modules, false for opt-in modules
      // like custom_feedback_forms — never show until tenant opts in).
      baseEnabled = item?.feature_default !== false;
    } else {
      baseEnabled = stored !== false;
    }
    if (!baseEnabled) return false;
    // iter180 — Parent cascade. If the parent group is OFF, the child is OFF.
    const parent = CHILD_TO_PARENT[f];
    if (parent && tenantFeatures?.[parent] === false) return false;
    return true;
  };
  // Module access:
  //   • admin / area_manager / super_admin → full access
  //   • legacy manager with empty assigned_modules → full access
  //   • everyone else → path must be (a) not module-gated OR (b) gated by at
  //     least one module in user.assigned_modules. Uses the SAME central
  //     `modulesForPath()` resolver that ProtectedRoute uses, so the sidebar
  //     can never disagree with the route guard (no more "module assigned but
  //     missing from sidebar" drift).
  const assignedModules = user?.assigned_modules || [];
  const isModuleAllowed = (to) => {
    if (ADMIN_LIKE_ROLES.includes(user?.role)) return true;
    // ITER308-B · scoped-admin (production_head + maintenance_manager)
    // sidebar filters by assigned_modules. Legacy production_head with
    // an empty list falls back to the recipes/pricing/production trio.
    if (SCOPED_ADMIN_ROLES.includes(user?.role)) {
      let mods = assignedModules;
      if (mods.length === 0 && user?.role === 'production_head') {
        mods = LEGACY_PRODUCTION_HEAD_MODS;
      }
      const ids = modulesForPath(to);
      if (ids.length === 0) return false;   // pure admin routes hidden
      return ids.some((id) => mods.includes(id));
    }
    if (ADMIN_ONLY_LINKS.has(to)) return false;
    const ids = modulesForPath(to);
    if (ids.length === 0) return true;  // not module-gated
    if (user?.role === 'manager' && assignedModules.length === 0) return true;
    return ids.some(id => assignedModules.includes(id));
  };
  const isOperatorAllowed = (to) => {
    const item = _navItemByPath[to];
    if (!item?.operator_only) return true;
    // Operator-only items are visible to super-admins and to admins of
    // the RestroPulse operator tenant (Amber Kitchen Group demo).
    return user?.role === 'super_admin'
        || user?.tenant_id === 'amber_kitchen_group';
  };
  const isLinkVisible = (to) => isFeatureAllowed(to) && isModuleAllowed(to)
                                 && isOperatorAllowed(to);

  // Listen for portfolio changes to update navbar logo
  useEffect(() => {
    const handler = (e) => {
      const pf = e.detail;
      setPortfolioLogo(pf?.logo_url || null);
    };
    window.addEventListener('portfolio-changed', handler);
    return () => window.removeEventListener('portfolio-changed', handler);
  }, []);

  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.pointerEvents = 'none';
      badge.style.cursor = 'default';
      if (user?.role === 'manager' || user?.role === 'supervisor') badge.style.display = 'none';
    }
    return () => { if (badge) { badge.style.display = ''; badge.style.pointerEvents = ''; } };
  }, [user?.role]);

  // Poll pending Petty Cash voucher count for approver roles. Drives the
  // small badge on the Operations menu so approvals don't get forgotten.
  useEffect(() => {
    if (!user) return undefined;
    const canApprove = ['admin', 'area_manager', 'super_admin'].includes(user.role);
    if (!canApprove) { setPendingPettyCash(0); return undefined; }
    let stopped = false;
    const fetchCount = async () => {
      try {
        // iter180p4 — switch from list-fetch-then-count (200 docs) to a
        // dedicated count endpoint (returns {count: N}). Saves ~99 % of
        // payload + lets Mongo serve from a covered index.
        const res = await fetch(`${API}/petty-cash/pending-count`, { headers: getHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!stopped) setPendingPettyCash(typeof data.count === 'number' ? data.count : 0);
      } catch { /* ignore — transient network errors are fine */ }
    };
    fetchCount();
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    // Also refresh when the petty cash page broadcasts an update.
    window.addEventListener('petty-cash-updated', onFocus);
    const id = setInterval(fetchCount, 60_000);
    return () => {
      stopped = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('petty-cash-updated', onFocus);
    };
  }, [user, API, getHeaders]);

  // Map of nav path → numeric badge count. Easy to extend in future.
  const navBadges = {
    '/admin/petty-cash': pendingPettyCash,
    '/manager/petty-cash': pendingPettyCash,
  };

  const outletLogoUrl = (user?.role === 'manager' || user?.role === 'supervisor') && user?.outlet_id && user?.has_logo
    ? `${API}/outlets/${user.outlet_id}/logo?t=${Date.now()}`
    : null;
  const tenantLogoUrl = tenantBranding?.has_logo && tenantBranding?.tenant_id
    ? `${API}/branding/tenant/${tenantBranding.tenant_id}/logo?v=${tenantBranding.logo_version || 0}`
    : null;

  // ITER308-D · Compute brand-theme CSS variables scoped to the shell.
  // Applied as `style={{'--color-primary': ...}}` on the layout root
  // so ONLY the admin/manager UI re-tints. Guest surfaces (loyalty
  // wallet, gift voucher) don't render inside Layout and keep the
  // platform's studio design.
  const brandVars = useMemo(
    () => deriveBrandVars(previewPrimaryColor || tenantBranding?.primary_color),
    [previewPrimaryColor, tenantBranding?.primary_color],
  );

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleSwitchOutlet = async (outletId) => {
    if (outletId === user?.outlet_id || switching) return;
    setSwitching(true);
    try {
      await switchOutlet(outletId);
      setOutletDropdownOpen(false);
      navigate(location.pathname, { replace: true });
      window.location.reload();
    } catch {} finally { setSwitching(false); }
  };

  // Outlet switcher is purely a function of assignment count — works for ANY
  // outlet-scoped role (manager / supervisor / chef / accountant / store_manager
  // / cashier / maintenance). Admins are NOT outlet-scoped so they don't see it.
  const showSwitcher = MANAGER_LIKE_ROLES.includes(user?.role) && assignedOutlets.length > 1;

  // Determine nav links based on role + tenant features + user module access
  const role = user?.role || '';
  const isAdminLike = ADMIN_LIKE_ROLES.includes(role);
  const isManagerLike = MANAGER_LIKE_ROLES.includes(role);
  const isProductionHead = role === 'production_head';
  const isSuperAdmin = role === 'super_admin';
  const isStudioUser = role === 'studio_user';

  let links;
  if (isStudioUser) {
    // Studio users see ONLY the Expansion Intelligence entry — they are a
    // dedicated consultant product, not a full-platform user.
    links = adminLinks.filter(l => l.to === '/admin/expansion-intelligence');
  } else if (isAdminLike) {
    links = adminLinks.filter(l => isLinkVisible(l.to));
    if (isSuperAdmin) links = [...links, ...superAdminExtraLinks];
  } else if (isProductionHead) {
    // Production Head renders admin-style UI but scoped to their assigned modules
    links = adminLinks.filter(l => isLinkVisible(l.to));
  } else if (isManagerLike) {
    links = managerLinks.filter(l => isLinkVisible(l.to));
  } else {
    links = [];
  }
  // Render bucketed dropdown nav for admins, production heads AND managers
  // (managers used to have a flat scrolling list — switching to grouped nav
  // matches the admin structure and reduces clutter). Studio users get a
  // single flat link, no grouping.
  const renderAsGroupedNav = !isStudioUser && (isAdminLike || isProductionHead || isManagerLike);
  const navGroupsForUser = isManagerLike ? managerNavGroups : adminNavGroups;

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-brand-scope="app-shell"
         style={brandVars || undefined}>
      <AiAlertBanner />
      <header className="bg-white border-b border-[#E2E8DF] sticky top-0 z-50 shadow-sm">
        {/* ═══ Single-row navbar: Brand · Context · Nav · Actions ═══ */}
        <div className="w-full px-3 sm:px-4 lg:max-w-[1400px] lg:mx-auto lg:px-6">
          <div className="flex items-center h-14 gap-3">
            {/* Brand */}
            <div className="flex items-center gap-2.5 shrink-0" data-testid="navbar-brand">
              {portfolioLogo ? (
                <img src={portfolioLogo} alt="Portfolio" className="h-8 w-auto max-w-[120px] object-contain" data-testid="portfolio-logo" />
              ) : outletLogoUrl ? (
                <img src={outletLogoUrl} alt="Outlet" className="h-7 w-7 object-cover rounded border border-[#E2E8DF]" data-testid="outlet-logo" />
              ) : tenantLogoUrl ? (
                <img src={tenantLogoUrl} alt="Tenant" className="h-8 w-auto max-w-[130px] object-contain" data-testid="tenant-logo" />
              ) : (
                <img src={platformLogoUrl} alt={platformName} className="h-8 w-auto max-w-[130px] object-contain" data-testid="default-logo" />
              )}
            </div>

            {/* Outlet switcher / context (compact) */}
            <div className="hidden md:flex items-center gap-3 shrink-0">
              {showSwitcher ? (
                <div className="relative" data-testid="outlet-switcher">
                  <button
                    onClick={() => setOutletDropdownOpen(!outletDropdownOpen)}
                    className="flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-[#E2E8DF] hover:border-[color:var(--color-primary)]/40 transition-colors bg-[#F9F9F8]"
                    data-testid="outlet-switcher-trigger"
                  >
                    <Building2 className="w-3.5 h-3.5 text-[#2D6A4F] shrink-0" />
                    <span className="text-[12px] font-semibold text-[#1A2421] max-w-[120px] truncate">{user?.outlet_name}</span>
                    <ChevronDown className={`w-3 h-3 text-[#5C6B64] transition-transform ${outletDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {outletDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setOutletDropdownOpen(false)} />
                      <div className="absolute left-0 top-full mt-1.5 bg-white border border-[#E2E8DF] rounded-lg shadow-xl z-50 min-w-[200px] py-1"
                        data-testid="outlet-switcher-dropdown">
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium border-b border-[#E2E8DF]">Switch Outlet</div>
                        {assignedOutlets.map(o => (
                          <button key={o.id} onClick={() => handleSwitchOutlet(o.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left transition-colors
                              ${o.id === user?.outlet_id
                                ? 'bg-[color:var(--color-primary)]/5 text-[color:var(--color-primary)] font-semibold'
                                : 'text-[#5C6B64] hover:bg-[#F3F5F2] hover:text-[#1A2421]'}`}
                            disabled={switching}
                            data-testid={`switch-outlet-${o.id}`}
                          >
                            <Building2 className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">{o.name}</span>
                            {o.id === user?.outlet_id && (
                              <span className="ml-auto text-[9px] bg-[#2D6A4F]/10 text-[#2D6A4F] px-1.5 py-0.5 rounded-full font-medium">Active</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : MANAGER_LIKE_ROLES.includes(user?.role) ? (
                <div className="flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#F9F9F8] border border-[#E2E8DF]" data-testid="user-outlet-pill">
                  <Building2 className="w-3.5 h-3.5 text-[#2D6A4F] shrink-0" />
                  <span className="text-[12px] font-semibold text-[#1A2421] max-w-[120px] truncate">{user?.outlet_name}</span>
                </div>
              ) : null}
            </div>

            {/* Nav (inline, scrollable). Feb 2026 · Overlap fix — nav is
                now horizontally scroll-clipped so items yield to the
                right-side actions (bell + logout) instead of overlapping.
                NavDropdown popovers use `fixed` positioning so they still
                render outside the scroll container. */}
            <nav className="hidden md:flex items-center flex-1 min-w-0 gap-0.5 overflow-x-auto scrollbar-hide"
              data-testid="admin-nav">
              {renderAsGroupedNav ? (
                <>
                  {navGroupsForUser.map(group => {
                    // iter180 — Parent feature gate. If the group declares a
                    // group_flag and it's OFF for this tenant, hide the entire
                    // group regardless of which children are individually on.
                    // Super admin always sees everything.
                    if (
                      group.group_flag
                      && user?.role !== 'super_admin'
                      && tenantFeatures?.[group.group_flag] === false
                    ) return null;
                    const filteredItems = (group.items || []).filter(i => isLinkVisible(i.to));
                    const filteredSubgroups = (group.subgroups || [])
                      .map(sg => ({ ...sg, items: sg.items.filter(i => isLinkVisible(i.to)) }))
                      .filter(sg => sg.items.length > 0);
                    if (filteredItems.length === 0 && filteredSubgroups.length === 0) return null;
                    return (
                      <NavDropdown key={group.label}
                                    group={{ ...group, items: filteredItems, subgroups: filteredSubgroups }}
                                    pathname={location.pathname} badges={navBadges} />
                    );
                  })}
                  {isSuperAdmin && superAdminExtraLinks.map(link => {
                    const Icon = link.icon;
                    const active = location.pathname === link.to;
                    return (
                      <Link key={link.to} to={link.to}>
                        <button
                          className={`flex items-center gap-1.5 whitespace-nowrap text-[12px] h-8 px-2.5 rounded-md transition-colors
                            ${active ? 'text-[color:var(--color-primary)] font-semibold bg-[color:var(--color-primary)]/[0.06]' : 'text-[#5C6B64] hover:text-[#1A2421] hover:bg-[#F3F5F2]'}`}
                          data-testid={`nav-${link.to.split('/').pop()}`}
                        >
                          <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                          {link.label}
                        </button>
                      </Link>
                    );
                  })}
                </>
              ) : (
                links.map(link => {
                  const Icon = link.icon;
                  const active = location.pathname === link.to;
                  return (
                    <Link key={link.to} to={link.to}>
                      <button
                        className={`flex items-center gap-1.5 whitespace-nowrap text-[12px] h-8 px-2.5 rounded-md transition-colors
                          ${active
                            ? 'text-[color:var(--color-primary)] font-semibold bg-[color:var(--color-primary)]/[0.06]'
                            : 'text-[#5C6B64] hover:text-[#1A2421] hover:bg-[#F3F5F2]'
                          }`}
                        data-testid={`nav-${link.to.split('/').pop()}`}
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                        {link.label}
                      </button>
                    </Link>
                  );
                })
              )}
            </nav>

            {/* Right — User / Logout / Mobile toggle. Identity block only
                shows at 2xl+ (≥1536) so nav has room to scroll on
                1440/1366/1280 without overlapping the right actions. */}
            <div className="flex items-center gap-1.5 shrink-0 ml-auto">
              <div className="hidden 2xl:flex items-center h-8 px-2 rounded-md bg-[#F9F9F8]">
                <div className="text-right">
                  <p className="text-[12px] font-semibold text-[#1A2421] leading-none">{user?.name}</p>
                  <p className="text-[10px] text-[#5C6B64] leading-tight mt-0.5">
                    {({'admin': 'Admin', 'area_manager': 'Area Manager', 'manager': 'Manager', 'supervisor': 'Supervisor', 'super_admin': 'Super Admin'})[user?.role] || user?.role}
                  </p>
                </div>
              </div>
              <DiscussionsBell onClick={() => navigate(user?.role === 'manager' ? '/manager/action-comm' : '/admin/action-comm')} />
              <Button
                variant="ghost" size="sm" onClick={handleLogout}
                className="text-[#5C6B64] hover:text-[#C45A41] hover:bg-[#C45A41]/5 h-8 w-8 p-0"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button
                variant="ghost" size="sm"
                className="md:hidden text-[#5C6B64] h-8 w-8 p-0"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* ═══ Mobile Menu ═══ */}
        {mobileOpen && (
          <div className="md:hidden border-t border-[#E2E8DF] bg-white p-3 space-y-1 max-h-[70vh] overflow-y-auto">
            <div className="px-3 py-2 text-sm text-[#5C6B64] border-b border-[#E2E8DF] mb-1">
              <span className="font-semibold text-[#1A2421]">{user?.name}</span>
              <span className="text-[#5C6B64]"> — {user?.role === 'admin' ? 'Admin' : user?.outlet_name}</span>
            </div>
            {showSwitcher && (
              <div className="px-3 py-2 border-b border-[#E2E8DF] mb-1">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium mb-1.5">Switch Outlet</p>
                <div className="space-y-0.5">
                  {assignedOutlets.map(o => (
                    <button key={o.id} onClick={() => { handleSwitchOutlet(o.id); setMobileOpen(false); }}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-left transition-colors
                        ${o.id === user?.outlet_id ? 'bg-[color:var(--color-primary)]/5 text-[color:var(--color-primary)] font-medium' : 'text-[#5C6B64] hover:bg-[#F3F5F2]'}`}
                      data-testid={`mobile-switch-outlet-${o.id}`}
                    >
                      <Building2 className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{o.name}</span>
                      {o.id === user?.outlet_id && (
                        <span className="ml-auto text-[9px] bg-[#2D6A4F]/10 text-[#2D6A4F] px-1.5 py-0.5 rounded-full">Active</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {renderAsGroupedNav ? (
              navGroupsForUser.map(group => {
                // iter180 — Parent feature gate (mobile menu mirror).
                if (
                  group.group_flag
                  && user?.role !== 'super_admin'
                  && tenantFeatures?.[group.group_flag] === false
                ) return null;
                const filteredItems = (group.items || []).filter(i => isLinkVisible(i.to));
                const subgroupsRender = (group.subgroups || [])
                  .map(sg => ({ ...sg, items: sg.items.filter(i => isLinkVisible(i.to)) }))
                  .filter(sg => sg.items.length > 0);
                if (filteredItems.length === 0 && subgroupsRender.length === 0) return null;
                return (
                <div key={group.label}>
                  <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium">{group.label}</p>
                  {subgroupsRender.map(sg => (
                    <div key={sg.label}>
                      <p className="px-4 pt-1 pb-0.5 text-[9px] uppercase tracking-[0.14em] text-[#5C6B64]/70">{sg.label}</p>
                      {sg.items.map(link => {
                        const Icon = link.icon;
                        const active = location.pathname === link.to;
                        return (
                          <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}>
                            <div className={`flex items-center gap-2.5 pl-5 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              active ? 'bg-[color:var(--color-primary)] text-white' : 'text-[#5C6B64] hover:bg-[#F3F5F2]'
                            }`}>
                              <Icon className="w-4 h-4" strokeWidth={1.5} />
                              {link.label}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ))}
                  {filteredItems.map(link => {
                    const Icon = link.icon;
                    const active = location.pathname === link.to;
                    return (
                      <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}>
                        <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          active ? 'bg-[color:var(--color-primary)] text-white' : 'text-[#5C6B64] hover:bg-[#F3F5F2]'
                        }`}>
                          <Icon className="w-4 h-4" strokeWidth={1.5} />
                          {link.label}
                        </div>
                      </Link>
                    );
                  })}
                </div>
                );
              })
            ) : (
              links.map(link => {
                const Icon = link.icon;
                const active = location.pathname === link.to;
                return (
                  <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}>
                    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      active ? 'bg-[color:var(--color-primary)] text-white' : 'text-[#5C6B64] hover:bg-[#F3F5F2]'
                    }`}>
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                      {link.label}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        )}
      </header>

      <main className="w-full px-3 sm:px-4 py-3 sm:py-4 lg:max-w-[1400px] lg:mx-auto lg:px-6 lg:py-5">
        {children}
      </main>
    </div>
  );
}
